"use client";

import React, { useState } from "react";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import {
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/20/solid";
import TandMCostPanel from "./TandMCostPanel";

// ── SizeInput (memoised) ──────────────────────────────────────────────────────
export const SizeInput = React.memo(
  ({ value, onCommit, placeholder, className }) => {
    const [localValue, setLocalValue] = React.useState(value);
    React.useEffect(() => {
      setLocalValue(value);
    }, [value]);
    return (
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => onCommit(localValue)}
        placeholder={placeholder}
        className={
          className ||
          "w-24 rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-2 py-1 font-mono"
        }
      />
    );
  },
);

// ── GroupedBudgetTable ────────────────────────────────────────────────────────
function GroupedBudgetTable({
  sortedAndFilteredItems,
  changeItems,
  pricingSections,
  wasApproved,
  isReadOnly,
  hasActiveFiltersOrSort,
  handleUpdateItem,
  handleRemoveItem,
  handleMoveItem,
  formatCurrency,
  isSFUnit,
  isLFUnit,
  isFromBudget,
  assemblyOptions,
  bidAreaOptions,
  referenceOptions,
  getLaborIdOptions,
  getSortIndicator,
  requestSort,
  changeOrderFilters,
  setChangeOrderFilters,
  showCOFilter,
  setShowCOFilter,
  coFilterRefs,
  getCOFilterOptions,
  customStyles,
  totals,
  onSectionAdd,
}) {
  const groups = React.useMemo(() => {
    const sectionGroups = pricingSections.map((s) => ({
      sectionNumber: s.number,
      title: s.title || `Section ${s.number}`,
      items: sortedAndFilteredItems.filter(
        (i) => i.referenceNumber === s.number,
      ),
    }));
    const unassigned = sortedAndFilteredItems.filter(
      (i) => i.referenceNumber == null,
    );
    return { sectionGroups, unassigned };
  }, [sortedAndFilteredItems, pricingSections]);

  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const renderRow = (item) => {
    const isEditable =
      (item.isFromDatabase || !item.sourceAssemblyId) && !wasApproved;
    const isCov =
      item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
    const coverage = parseFloat(item.coveragePerUnit) || 1;
    const pkgCost = isCov
      ? (item.unitCost || 0) * coverage
      : item.unitCost || 0;

    return (
      <tr key={item._localId} className="hover:bg-gray-600">
        <td className="px-2 py-2 text-gray-300">
          {item.customerDescription || ""}
        </td>
        <td className="px-2 py-2">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.category === "M" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}
          >
            {item.category === "M" ? "Mat" : "Lab"}
          </span>
        </td>
        <td className="px-2 py-2">
          {isEditable ? (
            <CreatableSelect
              options={assemblyOptions}
              value={
                assemblyOptions.find((o) => o.value === item.conditionName) ||
                (item.conditionName
                  ? { value: item.conditionName, label: item.conditionName }
                  : null)
              }
              onChange={(opt) =>
                handleUpdateItem(
                  item._localId,
                  "conditionName",
                  opt ? opt.value : "",
                )
              }
              onCreateOption={(v) =>
                handleUpdateItem(item._localId, "conditionName", v)
              }
              placeholder="Type or select assembly..."
              isClearable
              formatCreateLabel={(v) => `Create: "${v}"`}
              className="min-w-48"
              styles={customStyles}
              menuPortalTarget={document.body}
            />
          ) : (
            <span className="text-sm text-gray-300">
              {item.conditionName || "—"}
            </span>
          )}
        </td>
        <td className="px-2 py-2">
          {isEditable ? (
            <CreatableSelect
              options={bidAreaOptions}
              value={
                bidAreaOptions.find((o) => o.value === item.bidArea) ||
                (item.bidArea
                  ? { value: item.bidArea, label: item.bidArea }
                  : null)
              }
              onChange={(opt) =>
                handleUpdateItem(item._localId, "bidArea", opt ? opt.value : "")
              }
              onCreateOption={(v) =>
                handleUpdateItem(item._localId, "bidArea", v)
              }
              className="min-w-48"
              placeholder="Type or select bid area..."
              isClearable
              formatCreateLabel={(v) => `Create: "${v}"`}
              styles={customStyles}
              menuPortalTarget={document.body}
            />
          ) : (
            <span className="text-sm text-gray-300">{item.bidArea || "—"}</span>
          )}
        </td>
        <td className="px-2 py-2">
          {isEditable && isCov ? (
            <SizeInput
              value={item.size || ""}
              onCommit={(val) => handleUpdateItem(item._localId, "size", val)}
              placeholder={isSFUnit(item.uom) ? "4'x8'" : "10'"}
            />
          ) : isCov ? (
            <span className="text-xs text-gray-300 font-mono">
              {item.size || "-"}
            </span>
          ) : (
            <span className="text-xs text-gray-500">-</span>
          )}
        </td>
        <td className="px-2 py-2 text-right">
          <div className="flex flex-col items-end">
            <span
              className={`text-sm ${item.hasPriceChange ? "text-purple-400" : "text-gray-300"}`}
            >
              {formatCurrency(item.unitCost)}
            </span>
            {item.hasPriceChange && item.originalPrice !== item.unitCost && (
              <span className="text-[10px] text-gray-500 line-through">
                {formatCurrency(item.originalPrice)}
              </span>
            )}
          </div>
        </td>
        <td className="px-2 py-2 text-right text-sm text-gray-300">
          {item.uom}
        </td>
        <td className="px-2 py-2 text-right">
          <span className="text-sm font-medium text-orange-300">
            {formatCurrency(pkgCost)}
          </span>
        </td>
        <td className="px-2 py-2">
          <div className="flex flex-col gap-0.5">
            <input
              type="text"
              inputMode="decimal"
              value={
                item._rawDeltaInput !== undefined
                  ? item._rawDeltaInput
                  : item.deltaQty || 0
              }
              onChange={(e) =>
                handleUpdateItem(item._localId, "deltaQty", e.target.value)
              }
              onBlur={() => {
                if (
                  item._rawDeltaInput !== undefined ||
                  item.deltaQty === "" ||
                  item.deltaQty === "-"
                )
                  handleUpdateItem(item._localId, "deltaQty", "0");
              }}
              disabled={wasApproved}
              className={`w-20 px-2 py-1 text-right rounded border text-sm font-medium ${(item.deltaQty || 0) < 0 ? "text-red-400" : "text-gray-200"} ${wasApproved ? "bg-gray-700 opacity-60" : "bg-gray-600"} border-gray-500`}
            />
            {isFromBudget(item) &&
              (item.availableQty > 0 || item.remainingQty > 0) && (
                <span className="text-[9px] text-gray-500 text-center">{`min: -${item.availableQty ?? item.remainingQty ?? 0}`}</span>
              )}
          </div>
        </td>
        <td className="px-2 py-2 text-center">
          <span className="text-xs text-gray-300">
            {isCov ? "PC" : item.uom || "EA"}
          </span>
        </td>
        <td className="px-2 py-2 text-right">
          <span
            className={`text-sm font-medium ${item.amount < 0 ? "text-red-400" : "text-gray-200"}`}
          >
            {formatCurrency(item.amount)}
          </span>
        </td>
        <td className="px-2 py-2">
          <Select
            value={
              referenceOptions.find((o) => o.value === item.referenceNumber) ||
              referenceOptions[0]
            }
            onChange={(opt) =>
              handleUpdateItem(
                item._localId,
                "referenceNumber",
                opt?.value || null,
              )
            }
            options={referenceOptions}
            styles={customStyles}
            menuPortalTarget={document.body}
            isSearchable={false}
            className="w-28"
          />
        </td>
        <td className="px-2 py-2">
          {item.conditionName ? (
            isFromBudget(item) ? (
              <div className="text-sm text-gray-400 bg-gray-800 rounded px-3 py-1.5">
                {item.laborId || "—"}
              </div>
            ) : (
              <Select
                options={getLaborIdOptions(item.conditionName)}
                value={
                  getLaborIdOptions(item.conditionName).find(
                    (o) => o.value === item.laborId,
                  ) || null
                }
                onChange={(opt) =>
                  handleUpdateItem(item._localId, "laborId", opt?.value ?? null)
                }
                placeholder="Select / New"
                isClearable
                styles={customStyles}
                menuPortalTarget={document.body}
                className="w-18"
              />
            )
          ) : (
            <span className="text-xs text-gray-500">Select assembly first</span>
          )}
        </td>
        <td className="px-2 py-2 text-center">
          <button
            type="button"
            onClick={() =>
              handleUpdateItem(
                item._localId,
                "includeInBreakdown",
                !item.includeInBreakdown,
              )
            }
            disabled={!item.referenceNumber}
            className={`relative w-10 h-5 rounded-full transition-colors ${!item.referenceNumber ? "bg-gray-600 cursor-not-allowed opacity-50" : item.includeInBreakdown ? "bg-green-500" : "bg-gray-500"}`}
          >
            <span
              className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-transform ${item.includeInBreakdown ? "translate-x-0.5" : "-translate-x-4"}`}
            />
          </button>
        </td>
        <td className="px-2 py-2 text-center">
          {!hasActiveFiltersOrSort ? (
            <div className="flex flex-col items-center gap-0.5">
              <button
                type="button"
                onClick={() => handleMoveItem(item._localId, "up")}
                disabled={
                  changeItems.findIndex((i) => i._localId === item._localId) ===
                  0
                }
                className="text-gray-400 hover:text-orange-400 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronUpIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleMoveItem(item._localId, "down")}
                disabled={
                  changeItems.findIndex((i) => i._localId === item._localId) ===
                  changeItems.length - 1
                }
                className="text-gray-400 hover:text-orange-400 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronDownIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <span className="text-[10px] text-gray-500 italic">locked</span>
          )}
        </td>
        {!wasApproved && (
          <td className="px-2 py-2">
            <button
              type="button"
              onClick={() => handleRemoveItem(item._localId)}
              className="text-red-400 hover:text-red-500"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </td>
        )}
      </tr>
    );
  };

  const tableHeader = (
    <thead className="bg-gray-800 sticky top-0 z-10">
      <tr className="border-b border-gray-700">
        {[
          ["description", "Description", "text-left min-w-[150px]"],
          ["", "Cat", "text-center"],
          ["", "Assembly", "text-left min-w-[160px]"],
          ["", "Bid Area", "text-left min-w-[140px]"],
          ["", "Size", "text-left"],
          ["unitCost", "Unit Cost", "text-right"],
          ["", "Unit", "text-right"],
          ["pkgCost", "Pkg Cost", "text-right"],
          ["deltaQty", "Qty Δ", "text-right"],
          ["", "Pkg Unit", "text-center"],
          ["amount", "Total Cost", "text-right"],
          ["", "Ref #", "text-center"],
          ["", "Labor ID", "text-left"],
          ["", "Breakdown", "text-center"],
          ["", "Order", "text-center w-16"],
        ].map(([key, label, align]) => (
          <th
            key={label}
            className={`px-3 py-3 ${align} text-xs font-semibold text-gray-300 uppercase tracking-wider ${key ? "cursor-pointer hover:bg-gray-700/70" : ""}`}
            onClick={key ? () => requestSort(key) : undefined}
          >
            {label}
            {key && getSortIndicator(key) && (
              <span className="ml-1 text-orange-400 font-bold">
                {getSortIndicator(key)}
              </span>
            )}
          </th>
        ))}
        {!wasApproved && <th className="px-3 py-3 w-12" />}
      </tr>
    </thead>
  );

  const renderGroup = (sectionNumber, title, items, color = "orange") => {
    const key = sectionNumber ?? "unassigned";
    const isCollapsed = collapsedGroups.has(key);
    const subtotal = items.reduce((s, i) => s + (i.amount || 0), 0);
    const headerClass =
      color === "orange"
        ? "bg-orange-500/10 border-orange-500/30 text-orange-400"
        : "bg-gray-600/40 border-gray-500/40 text-gray-400";
    return (
      <React.Fragment key={key}>
        <tr
          className={`border-t border-b ${color === "gray" ? "border-gray-500/30" : "border-orange-500/20"}`}
        >
          <td colSpan={wasApproved ? 15 : 16} className="px-3 py-2">
            <div
              className={`flex items-center justify-between rounded px-3 py-1.5 border ${headerClass}`}
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleGroup(key)}
                  className="text-current opacity-70 hover:opacity-100"
                >
                  {isCollapsed ? (
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronUpIcon className="h-3.5 w-3.5" />
                  )}
                </button>
                {sectionNumber != null ? (
                  <span className="text-xs font-semibold">
                    Section {sectionNumber}
                    {title !== `Section ${sectionNumber}` ? ` — ${title}` : ""}
                  </span>
                ) : (
                  <span className="text-xs font-semibold">Unassigned</span>
                )}
                <span className="text-[10px] opacity-70">
                  {items.length} item{items.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {sectionNumber != null && !wasApproved && !isReadOnly && (
                  <div className="flex gap-1">
                    {[
                      [
                        "budget",
                        "Budget",
                        "text-orange-300 bg-orange-500/10 border-orange-500/20 hover:bg-orange-500/20",
                      ],
                      [
                        "database",
                        "Database",
                        "text-blue-300 bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20",
                      ],
                      [
                        "excel",
                        "Excel",
                        "text-purple-300 bg-purple-500/10 border-purple-500/20 hover:bg-purple-500/20",
                      ],
                    ].map(([type, label, cls]) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => onSectionAdd(sectionNumber, type)}
                        className={`px-2 py-0.5 text-[10px] font-medium border rounded flex items-center gap-0.5 ${cls}`}
                      >
                        <PlusIcon className="h-3 w-3" />
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <span
                  className={`text-xs font-semibold tabular-nums ${subtotal < 0 ? "text-red-400" : ""}`}
                >
                  {formatCurrency(subtotal)}
                </span>
              </div>
            </div>
          </td>
        </tr>
        {!isCollapsed &&
          (items.length === 0 ? (
            <tr>
              <td
                colSpan={wasApproved ? 15 : 16}
                className="px-6 py-3 text-center text-xs text-gray-500 italic"
              >
                {sectionNumber != null
                  ? "No items assigned to this section yet."
                  : "No unassigned items."}
              </td>
            </tr>
          ) : (
            items.map(renderRow)
          ))}
      </React.Fragment>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-600">
        {tableHeader}
        <tbody className="divide-y divide-gray-600">
          {groups.sectionGroups.map((g) =>
            renderGroup(g.sectionNumber, g.title, g.items, "orange"),
          )}
          {renderGroup(null, "Unassigned", groups.unassigned, "gray")}
        </tbody>
      </table>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function COBudgetItemsSection({
  isTandMBased,
  tandmId,
  tandmNumber,
  wasApproved,
  isReadOnly,
  tandmCostData,
  setTandmCostData,
  setShowUnlinkConfirm,
  projectId,
  token,
  budgetItemsCollapsed,
  setBudgetItemsCollapsed,
  setShowBudgetModal,
  setShowDatabaseModal,
  setShowExcelUploadModal,
  setAddingToSection,
  changeItems,
  pricingSections,
  totals,
  filteredChangeItems,
  displayItems,
  changeOrderFilters,
  setChangeOrderFilters,
  hasActiveFiltersOrSort,
  formatCurrency,
  ...tableProps
}) {
  // Cost of any extra items the PM added directly in this CO (not from T&M).
  // After approval these items have been merged into the T&M budget, so
  // tandmCostData.totals.totalCost already includes them — do NOT add them
  // again to avoid double-counting.
  const newScopeCost = isTandMBased
    ? changeItems.reduce((sum, item) => {
        if (!item.forBudget) return sum;
        const sq = item.deltaQty || item.quantity || 0;
        const qty = Math.abs(sq);
        const cov = parseFloat(item.coveragePerUnit) || 1;
        const isCov =
          item.category === "M" &&
          (tableProps.isSFUnit?.(item.uom) || tableProps.isLFUnit?.(item.uom));
        const cost =
          item.category !== "M" && item.unitRate > 0
            ? item.unitRate
            : item.unitCost || 0;
        const total = isCov ? qty * cov * cost : qty * cost;
        return sum + (sq < 0 ? -total : total);
      }, 0)
    : 0;

  const tandmBaseCost = tandmCostData?.totals?.totalCost || 0;

  // After approval the additional items are already inside tandmBaseCost,
  // so the total CO cost is simply the T&M total. Before approval we add
  // the pending new-scope items on top of the locked T&M base.
  const totalCOCost = isTandMBased
    ? wasApproved
      ? tandmBaseCost
      : tandmBaseCost + newScopeCost
    : totals.budgetCost;

  const showTable = isTandMBased ? !wasApproved : !budgetItemsCollapsed;

  return (
    <div
      className={`bg-gray-700 p-4 rounded-lg border ${isTandMBased ? "border-teal-600/40" : "border-gray-600"}`}
    >
      {/* ── Header ── */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          {!isTandMBased && (
            <button
              type="button"
              onClick={() => setBudgetItemsCollapsed((p) => !p)}
              className="p-1 text-gray-400 hover:text-orange-400 hover:bg-gray-600 rounded"
            >
              {budgetItemsCollapsed ? (
                <ChevronDownIcon className="h-5 w-5" />
              ) : (
                <ChevronUpIcon className="h-5 w-5" />
              )}
            </button>
          )}
          <h2 className="text-lg font-semibold text-white">
            {isTandMBased
              ? "Additional CO Items"
              : "Budget Items (Cost Tracking)"}
            {wasApproved && !isTandMBased && (
              <span className="ml-2 text-sm font-normal text-red-400">
                — Locked
              </span>
            )}
            {isTandMBased && tandmNumber && (
              <span className="ml-2 text-sm font-normal text-teal-400">
                {wasApproved
                  ? `— merged into T&M #${tandmNumber}`
                  : `— merged into T&M #${tandmNumber} on approval`}
              </span>
            )}
          </h2>
          {!isTandMBased && budgetItemsCollapsed && changeItems.length > 0 && (
            <span className="text-sm text-gray-400">
              {changeItems.length} item{changeItems.length !== 1 ? "s" : ""} —{" "}
              <span
                className={
                  totals.budgetCost >= 0 ? "text-green-400" : "text-red-400"
                }
              >
                {formatCurrency(totals.budgetCost)}
              </span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isTandMBased && !wasApproved && (
            <button
              type="button"
              onClick={() => setShowUnlinkConfirm(true)}
              className="text-xs text-gray-500 hover:text-red-400 underline"
            >
              Unlink T&M
            </button>
          )}
          {!wasApproved && !isReadOnly && (
            <>
              {isTandMBased ? (
                <span className="text-[11px] text-teal-400/80 italic mr-1">
                  Items added here → T&M #{tandmNumber} budget on approval:
                </span>
              ) : (
                <span className="text-xs text-gray-500 italic">
                  Unassigned adds:
                </span>
              )}
              {[
                {
                  key: "budget",
                  label: "Budget",
                  cls: "text-orange-300 bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20",
                  icon: <PlusIcon className="h-3 w-3" />,
                  onClick: () => {
                    setAddingToSection(null);
                    setShowBudgetModal(true);
                  },
                },
                {
                  key: "database",
                  label: "Database",
                  cls: "text-blue-300 bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20",
                  icon: <PlusIcon className="h-3 w-3" />,
                  onClick: () => {
                    setAddingToSection(null);
                    setShowDatabaseModal(true);
                  },
                },
                {
                  key: "excel",
                  label: "Excel",
                  cls: "text-purple-300 bg-purple-500/10 border-purple-500/20 hover:bg-purple-500/20",
                  icon: <ArrowUpTrayIcon className="h-3 w-3" />,
                  onClick: () => {
                    setAddingToSection(null);
                    setShowExcelUploadModal(true);
                  },
                },
              ].map(({ key, label, cls, icon, onClick }) => (
                <button
                  key={key}
                  type="button"
                  onClick={onClick}
                  className={`px-3 py-1.5 text-xs border rounded flex items-center gap-1 ${cls}`}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── T&M base cost panel ── */}
      {isTandMBased && (
        <div className="mb-4">
          <TandMCostPanel
            projectId={projectId}
            tandmId={tandmId}
            token={token}
            onLoad={(data) => setTandmCostData(data)}
          />
        </div>
      )}

      {/* ── T&M info banner + running total pill ── */}
      {isTandMBased && (
        <div className="mb-4 rounded-lg border border-teal-600/30 bg-teal-900/15 px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <svg
                className="mt-0.5 h-4 w-4 shrink-0 text-teal-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-medium text-teal-300 text-sm">
                  T&M #{tandmNumber || "Linked"} cost{" "}
                  {wasApproved
                    ? "is locked (shown above)."
                    : "is locked (shown above)."}
                </p>
                <p className="mt-0.5 text-teal-200/70 text-xs">
                  {wasApproved ? (
                    // After approval: additional items are already inside the T&M total
                    changeItems.length > 0 ? (
                      <>
                        <span className="text-orange-300 font-medium">
                          {changeItems.length} additional item
                          {changeItems.length !== 1 ? "s" : ""} (
                          {formatCurrency(newScopeCost)})
                        </span>{" "}
                        have been merged into T&M #{tandmNumber}.
                      </>
                    ) : (
                      "No additional items were added to this CO."
                    )
                  ) : changeItems.length > 0 ? (
                    <>
                      <span className="text-orange-300 font-medium">
                        {changeItems.length} additional item
                        {changeItems.length !== 1 ? "s" : ""} (
                        {formatCurrency(newScopeCost)})
                      </span>{" "}
                      will be merged into the T&M budget on approval.
                    </>
                  ) : (
                    "Use the add buttons above to include extra scope items. They will be added to the T&M budget on approval."
                  )}
                  {pricingSections.length > 0 && (
                    <span className="block mt-1 text-teal-300/60">
                      T&M cost breakdown is shown inline under each pricing
                      section's Budget Items tab below.
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Running total pill */}
            <div className="shrink-0 bg-gray-800/60 rounded-lg px-3 py-2 border border-teal-700/30 min-w-[185px]">
              {wasApproved ? (
                // After approval: T&M total already includes the merged items —
                // show only the single combined figure to avoid double-counting.
                <div className="flex justify-between gap-3 text-sm font-bold text-white">
                  <span>Total CO Cost</span>
                  <span className="tabular-nums">
                    {formatCurrency(tandmBaseCost)}
                  </span>
                </div>
              ) : (
                // Before approval: show the breakdown (T&M base + pending items).
                <>
                  <div className="flex justify-between gap-3 text-xs text-teal-400">
                    <span>T&M Cost Base</span>
                    <span className="tabular-nums font-medium">
                      {formatCurrency(tandmBaseCost)}
                    </span>
                  </div>
                  {newScopeCost !== 0 && (
                    <div className="flex justify-between gap-3 text-xs text-orange-400 mt-1">
                      <span>+ Additional Items</span>
                      <span className="tabular-nums font-medium">
                        {formatCurrency(newScopeCost)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between gap-3 text-sm font-bold text-white mt-2 pt-1.5 border-t border-gray-600">
                    <span>Total CO Cost</span>
                    <span className="tabular-nums">
                      {formatCurrency(totalCOCost)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Items table ── */}
      {showTable && (
        <>
          {changeItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-600 py-8 text-center">
              {isTandMBased ? (
                <>
                  <p className="text-sm text-gray-500">
                    No additional items added yet.
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    Use Budget, Database, or Excel above to add extra scope
                    items on top of the T&M cost.
                  </p>
                </>
              ) : (
                <p className="text-gray-400">No items added yet.</p>
              )}
            </div>
          ) : (
            <>
              <div className="bg-gray-800 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-4">
                  <span className="text-lg font-medium text-white">
                    {isTandMBased
                      ? changeItems.length
                      : filteredChangeItems.length}{" "}
                    item
                    {(isTandMBased
                      ? changeItems.length
                      : filteredChangeItems.length) !== 1
                      ? "s"
                      : ""}
                    {!isTandMBased &&
                      filteredChangeItems.length !== changeItems.length && (
                        <span className="text-gray-500 ml-2">
                          / {changeItems.length.toLocaleString()}
                        </span>
                      )}
                  </span>
                  <span className="text-gray-600">|</span>
                  <span
                    className={`font-medium ${isTandMBased ? "text-orange-400" : "text-green-400"}`}
                  >
                    {formatCurrency(
                      isTandMBased ? newScopeCost : totals.budgetCost,
                    )}
                  </span>
                </div>
                {!isTandMBased && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <input
                      type="text"
                      value={changeOrderFilters.description}
                      onChange={(e) =>
                        setChangeOrderFilters((p) => ({
                          ...p,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Search description..."
                      className="w-64 px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500"
                    />
                    {[
                      ["category", "Category", "teal"],
                      ["condition", "Assembly", "purple"],
                      ["bidArea", "Bid Area", "green"],
                      ["uom", "UOM", "indigo"],
                      ["size", "Size", "cyan"],
                      ["reference", "Ref #", "orange"],
                      ["laborId", "Labor ID", "pink"],
                      ["itemCode", "Item Code", "yellow"],
                    ].map(
                      ([key, label, color]) =>
                        changeOrderFilters[key]?.length > 0 && (
                          <span
                            key={key}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-${color}-600/80 text-white rounded`}
                          >
                            {label}:{" "}
                            {changeOrderFilters[key].length === 1
                              ? String(
                                  changeOrderFilters[key][0] ?? "Unassigned",
                                ).substring(0, 20)
                              : `${changeOrderFilters[key].length} selected`}
                            <button
                              type="button"
                              onClick={() =>
                                setChangeOrderFilters((p) => ({
                                  ...p,
                                  [key]: [],
                                }))
                              }
                              className="hover:opacity-70"
                            >
                              ×
                            </button>
                          </span>
                        ),
                    )}
                    {Object.values(changeOrderFilters).some((v) =>
                      Array.isArray(v) ? v.length > 0 : !!v,
                    ) && (
                      <button
                        type="button"
                        onClick={() =>
                          setChangeOrderFilters({
                            itemCode: [],
                            description: "",
                            category: [],
                            condition: [],
                            bidArea: [],
                            size: [],
                            uom: [],
                            reference: [],
                            laborId: [],
                          })
                        }
                        className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}
              </div>

              <GroupedBudgetTable
                sortedAndFilteredItems={displayItems}
                changeItems={changeItems}
                pricingSections={pricingSections}
                wasApproved={wasApproved}
                isReadOnly={isReadOnly}
                hasActiveFiltersOrSort={
                  isTandMBased ? false : hasActiveFiltersOrSort
                }
                formatCurrency={formatCurrency}
                changeOrderFilters={changeOrderFilters}
                setChangeOrderFilters={setChangeOrderFilters}
                {...tableProps}
              />

              {!isTandMBased && changeItems.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <div className="bg-gray-800 rounded-lg px-4 py-2 border border-gray-600">
                    <span className="text-sm text-gray-400 mr-3">
                      Total Budget Cost:
                    </span>
                    <span
                      className={`text-lg font-bold ${totals.budgetCost >= 0 ? "text-green-400" : "text-red-400"}`}
                    >
                      {formatCurrency(totals.budgetCost)}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
